import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import ProjectsHubBackLink from './hub/ProjectsHubBackLink';
import ItemsPanel from './ProjectEdit/ItemsPanel';
import ItemMetadataSettingsPanel from './ProjectEdit/ItemMetadataSettingsPanel';
import FolderAccessModal from './FolderAccessModal';
import { IconPencil } from './hub/HubRowActionIcons';
import RenameResourceModal from './hub/RenameResourceModal';
import styles from './ProjectDetail.module.css';
import projectEditStyles from './ProjectEditForm.module.css';

const ACCESS_WRITE = 1;
const ACCESS_ADMIN = 2;

/**
 * Slim settings page for Slide Sets: name, items, item metadata, sharing.
 * No annotationUI / participants / publish / ROI / notes.
 */
function SlideSetDetail() {
    const { folderId } = useParams();
    const navigate = useNavigate();

    const [doc, setDoc] = useState(null);
    const [itemMetadataRoot, setItemMetadataRoot] = useState({ itemMetadata: {} });
    const [instructions, setInstructions] = useState('');
    const [items, setItems] = useState([]);
    const [subfolders, setSubfolders] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(true);
    const [itemsError, setItemsError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [accessOpen, setAccessOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [deleteConfirmName, setDeleteConfirmName] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const canWrite = typeof doc?.myAccess === 'number' && doc.myAccess >= ACCESS_WRITE;
    const canAdmin = typeof doc?.myAccess === 'number' && doc.myAccess >= ACCESS_ADMIN;

    const savedItemMetadata = useMemo(
        () => doc?.girderProjects?.slideSet?.itemMetadata ?? {},
        [doc],
    );
    const savedInstructions = useMemo(
        () => String(doc?.girderProjects?.slideSet?.instructions ?? ''),
        [doc],
    );

    const isDirty = useMemo(() => {
        try {
            const metaDirty = JSON.stringify(itemMetadataRoot.itemMetadata || {})
                !== JSON.stringify(savedItemMetadata || {});
            const instructionsDirty = instructions !== savedInstructions;
            return metaDirty || instructionsDirty;
        } catch {
            return true;
        }
    }, [itemMetadataRoot, savedItemMetadata, instructions, savedInstructions]);

    const loadDoc = useCallback(() => {
        if (!folderId) return;
        setLoading(true);
        setError(null);
        api.get(`projects/${folderId}`, {})
            .then((r) => {
                if (r.status !== 200) {
                    setError(r.data?.message || 'Failed to load Slide Set.');
                    setDoc(null);
                    return;
                }
                const folder = r.data;
                if (folder?.girderProjects?.type !== 'slideset') {
                    setError('This folder is not a Slide Set.');
                    setDoc(null);
                    return;
                }
                setDoc(folder);
                const slideSet = folder.girderProjects?.slideSet || {};
                setInstructions(String(slideSet.instructions ?? ''));
                setItemMetadataRoot({
                    itemMetadata: slideSet.itemMetadata || {
                        rows: [],
                        itemMetadataColumns: [],
                        trimWhitespace: true,
                        caseInsensitive: false,
                    },
                });
            })
            .catch((e) => {
                setError(e.message || 'Failed to load Slide Set.');
                setDoc(null);
            })
            .finally(() => setLoading(false));
    }, [folderId]);

    const loadItems = useCallback(() => {
        if (!folderId) return;
        setItemsLoading(true);
        setItemsError(null);
        api.get(`projects/${folderId}/items`, {})
            .then((r) => {
                if (r.status === 200) {
                    const list = r.data?.items || [];
                    setItems(list);
                    setSubfolders(r.data?.subfolders || []);
                } else {
                    setItemsError(r.data?.message || 'Failed to load items.');
                }
            })
            .catch((e) => setItemsError(e.message || 'Failed to load items.'))
            .finally(() => setItemsLoading(false));
    }, [folderId]);

    useEffect(() => {
        loadDoc();
    }, [loadDoc]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    function handleSave() {
        if (!canWrite || !isDirty) return;
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        api.put(`projects/${folderId}`, {
            json: {
                slideSet: {
                    itemMetadata: itemMetadataRoot.itemMetadata || {},
                    instructions,
                },
            },
        })
            .then((r) => {
                if (r.status === 200) {
                    setDoc(r.data);
                    const slideSet = r.data?.girderProjects?.slideSet || {};
                    setInstructions(String(slideSet.instructions ?? ''));
                    setItemMetadataRoot({
                        itemMetadata: slideSet.itemMetadata || {},
                    });
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 2500);
                } else {
                    setSaveError(r.data?.message || 'Save failed.');
                }
            })
            .catch((e) => setSaveError(e.message || 'Save failed.'))
            .finally(() => setSaving(false));
    }

    function handleDelete() {
        if (!doc || deleteConfirmName !== doc.name) return;
        api.delete(`projects/${folderId}`, {})
            .then((r) => {
                if (r.status === 200) navigate('/projects/manage');
                else setSaveError(r.data?.message || 'Delete failed.');
            })
            .catch((e) => setSaveError(e.message || 'Delete failed.'));
    }

    function handleConfirmDelete() {
        if (!confirmDelete) return;
        const { type, id } = confirmDelete;
        const path = type === 'subfolder'
            ? `projects/folders/${id}`
            : `projects/${folderId}/items/${id}`;
        api.delete(path, {})
            .then((r) => {
                if (r.status === 200) loadItems();
                else setItemsError(r.data?.message || 'Delete failed.');
            })
            .catch((e) => setItemsError(e.message || 'Delete failed.'))
            .finally(() => setConfirmDelete(null));
    }

    if (loading) {
        return <p className={styles.loading}>Loading…</p>;
    }

    if (error || !doc) {
        return (
            <div>
                <ProjectsHubBackLink />
                <p className={styles.errorMessage}>{error || 'Slide Set not found.'}</p>
            </div>
        );
    }

    return (
        <div>
            <ProjectsHubBackLink />
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <div className={styles.titleRow}>
                        <h1 className={styles.pageTitle}>{doc.name}</h1>
                        {canWrite ? (
                            <button
                                type="button"
                                className={styles.titleRenameBtn}
                                title="Rename"
                                aria-label="Rename Slide Set"
                                onClick={() => setRenameOpen(true)}
                            >
                                <IconPencil />
                            </button>
                        ) : null}
                    </div>
                    <p className={styles.breadcrumbCurrent}>Slide Set · settings</p>
                </div>
                <div className={styles.headerActions}>
                    <Link
                        className={styles.buttonOpen}
                        to={`/slidesets/${folderId}`}
                    >
                        Open Slide Set
                    </Link>
                    {canAdmin ? (
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => setAccessOpen(true)}
                        >
                            Sharing
                        </button>
                    ) : null}
                    {canWrite ? (
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    ) : null}
                </div>
            </div>
            {saveError ? <p className={styles.errorMessage}>{saveError}</p> : null}
            {saveSuccess ? <p className={styles.successMessage}>Saved.</p> : null}

            <div className={projectEditStyles.form}>
                <section className={projectEditStyles.panel} aria-label="Description">
                    <h4 className={projectEditStyles.panelTitle}>Description</h4>
                    <p style={{ margin: '0 0 0.75rem', color: '#586069', fontSize: '0.9em' }}>
                        Shown on the Slide Set landing page and in the viewer ⓘ popover.
                    </p>
                    <textarea
                        id="slideset-instructions"
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        disabled={!canWrite}
                        rows={5}
                        placeholder="Optional description for viewers…"
                        style={{
                            display: 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '0.5em 0.6em',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            fontSize: '0.95em',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            lineHeight: 1.45,
                        }}
                    />
                </section>

                <ItemsPanel
                    folderId={folderId}
                    items={items}
                    subfolders={subfolders}
                    loading={itemsLoading}
                    error={itemsError}
                    onReload={loadItems}
                    onSetConfirmDelete={setConfirmDelete}
                    settingsLocked={!canWrite}
                    subfolderType="slideset-subfolder"
                />

                <ItemMetadataSettingsPanel
                    itemMetadataRoot={itemMetadataRoot}
                    setItemMetadataRoot={setItemMetadataRoot}
                    projectItems={items}
                    folderId={folderId}
                    onReloadProjectItems={loadItems}
                    settingsLocked={!canWrite}
                />

                {canAdmin ? (
                    <section className={projectEditStyles.panel} aria-label="Danger zone">
                        <h4 className={projectEditStyles.panelTitle}>Delete Slide Set</h4>
                        <p style={{ margin: '0 0 0.75rem', color: '#586069', fontSize: '0.9em' }}>
                            Type the Slide Set name to confirm deletion.
                        </p>
                        <input
                            style={{
                                display: 'block',
                                width: '100%',
                                maxWidth: '28rem',
                                marginBottom: '0.75rem',
                                padding: '0.4em 0.6em',
                                border: '1px solid #d0d7de',
                                borderRadius: 4,
                            }}
                            value={deleteConfirmName}
                            onChange={(e) => setDeleteConfirmName(e.target.value)}
                            placeholder={doc.name}
                        />
                        <button
                            type="button"
                            className={projectEditStyles.deleteItemButton}
                            disabled={deleteConfirmName !== doc.name}
                            onClick={handleDelete}
                        >
                            Delete permanently
                        </button>
                    </section>
                ) : null}
            </div>

            <FolderAccessModal
                isOpen={accessOpen}
                onClose={() => setAccessOpen(false)}
                folderId={folderId}
                folderName={doc.name}
                folderType="slideset"
                allowPublicToggle
                onSaved={loadDoc}
            />

            <RenameResourceModal
                open={renameOpen}
                folderId={folderId}
                initialName={doc.name}
                onClose={() => setRenameOpen(false)}
                onSuccess={(data) => {
                    setDoc((prev) => (prev ? { ...prev, ...data, name: data.name } : prev));
                    setRenameOpen(false);
                }}
            />

            {confirmDelete ? (
                <div className={projectEditStyles.confirmOverlay}>
                    <div className={projectEditStyles.confirmDialog}>
                        <p className={projectEditStyles.confirmMsg}>
                            {confirmDelete.type === 'item'
                                ? <>Delete item <strong>{confirmDelete.name}</strong>? This cannot be undone.</>
                                : (
                                    <>
                                        Delete subfolder <strong>{confirmDelete.name}</strong> and all
                                        its items? This cannot be undone.
                                    </>
                                )}
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setConfirmDelete(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonDanger}
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default SlideSetDetail;
